/**
 * OFFERING WIZARD (the marketplace listing flow, sponsor side).
 *
 * The sponsor decides how much of THEIR allocation to bring to market, and on what terms. But they do
 * not publish: they REQUEST publication, and the Trust approves before the offering lists. This is the
 * institutional difference from "user hits Sell" and the reason the final button says Request, not List.
 *
 * An offering can only draw from the sponsor's own treasury, so the token count is capped at what they
 * hold, and the wizard says so rather than letting a sponsor promise tokens they do not have.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type SponsorAsset } from '@/lib/api';
import { LoadingCard, ErrorState } from '@/components/states';
import { Reveal } from '@/components/motion';

const num = (n: number) => n.toLocaleString('en-KE');

export default function NewOffering() {
  const params = useParams();
  const navigate = useNavigate();
  // React Router types a param as possibly absent; this route cannot match without one.
  const id = params.assetId ?? '';

  const [asset, setAsset] = useState<SponsorAsset | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [tokens, setTokens] = useState('');
  const [price, setPrice] = useState('');
  const [opens, setOpens] = useState('');
  const [closes, setCloses] = useState('');
  const [story, setStory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    setLoadErr(null);
    try {
      const a = await api.sponsor.asset(id);
      setAsset(a);
      setName(`Offering #${a.offerings.length + 1}`);
      setPrice(a.capital?.tokenPrice ?? '');
    } catch (e) {
      setLoadErr(e instanceof LipaApiError ? e.detail.message : 'We could not load this asset.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loadErr) return <ErrorState body={loadErr} onRetry={() => void load()} />;
  if (!asset) return <LoadingCard lines={4} />;

  const available = asset.treasury?.sponsorTreasury ?? 0;
  const tokenN = Number(tokens.replace(/,/g, '')) || 0;
  const overAllocated = tokenN > available;
  const valid = name.trim() && tokenN > 0 && !overAllocated && !!price && !!opens && !!closes;

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      await api.sponsor.requestOffering(id, {
        name: name.trim(),
        tokens: tokenN,
        price,
        opens,
        closes,
        story: story.trim(),
      });
      setDone(true);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not submit this offering.');
    } finally {
      setSubmitting(false);
    }
  }

  if (done) return <Requested assetId={id} onDone={() => navigate(`/tokenize/assets/${id}`)} />;

  const estRaise =
    tokenN && price ? num(Math.round(tokenN * Number(price.replace(/,/g, '')))) : '-';

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to={`/tokenize/assets/${id}`}
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <BackIcon /> {asset.name}
      </Link>

      <header>
        <h1 className="text-page-title text-ink">Create an offering</h1>
        <p className="mt-0.5 text-sm text-ink-muted">
          Bring part of your allocation to market. The trust approves it before it lists.
        </p>
      </header>

      <Reveal>
        <section className="rounded-card border border-border bg-surface p-5 shadow-card">
          <div className="mb-4 flex items-center justify-between rounded-card bg-surface-sunken px-3 py-2.5">
            <span className="text-sm text-ink-muted">Available in your treasury</span>
            <span className="tabular text-sm font-semibold text-ink">{num(available)} tokens</span>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Offering name" className="sm:col-span-2">
              <TextInput value={name} onChange={setName} />
            </Field>

            <Field
              label="Tokens to offer"
              hint={`Up to ${num(available)}`}
              error={overAllocated ? 'More than you hold' : undefined}
            >
              <TextInput value={tokens} onChange={setTokens} inputMode="numeric" placeholder="0" />
            </Field>

            <Field label="Price per token" hint="KES">
              <div className="flex h-11 items-center rounded-field border border-border bg-surface px-3 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                <span className="mr-2 text-sm text-ink-subtle">KES</span>
                <input
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  inputMode="decimal"
                  className="tabular h-full w-full bg-transparent text-base text-ink outline-none"
                />
              </div>
            </Field>

            <Field label="Opening date">
              <DateInput value={opens} onChange={setOpens} />
            </Field>
            <Field label="Closing date">
              <DateInput value={closes} onChange={setCloses} />
            </Field>

            <Field
              label="Investment story"
              hint="What investors see on the listing"
              className="sm:col-span-2"
            >
              <textarea
                value={story}
                onChange={(e) => setStory(e.target.value)}
                rows={4}
                placeholder="Why this offering, what the capital funds, and what investors earn."
                className="w-full rounded-field border border-border bg-surface px-3 py-2.5 text-base text-ink outline-none transition placeholder:text-ink-subtle focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </Field>
          </div>

          <div className="mt-4 flex items-center justify-between rounded-card bg-primary-tint px-3 py-2.5">
            <span className="text-sm text-ink">Estimated raise</span>
            <span className="tabular text-base font-semibold text-brand">KES {estRaise}</span>
          </div>
        </section>
      </Reveal>

      {error ? (
        <p className="rounded-field bg-danger/10 px-3 py-2 text-sm text-danger">{error}</p>
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <Link
          to={`/tokenize/assets/${id}`}
          className="rounded-field border border-border px-4 py-2.5 text-sm font-medium text-ink transition hover:bg-surface-sunken"
        >
          Cancel
        </Link>
        <button
          onClick={submit}
          disabled={!valid || submitting}
          className="rounded-field bg-primary px-5 py-2.5 text-sm font-medium text-white transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-40"
        >
          {submitting ? 'Submitting...' : 'Request publication'}
        </button>
      </div>
    </div>
  );
}

function Requested({ assetId, onDone }: { assetId: string; onDone: () => void }) {
  return (
    <div className="mx-auto max-w-md space-y-6 py-8 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-warning/15 text-warning">
        <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.8}
            d="M12 6v6l4 2m6-2a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z"
          />
        </svg>
      </div>
      <div>
        <h1 className="text-page-title text-ink">Offering requested</h1>
        <p className="mt-1 text-sm text-ink-muted">
          The trust is reviewing it. Once approved, it lists on the marketplace and investors can
          buy in.
        </p>
      </div>
      <button
        onClick={onDone}
        className="w-full rounded-field bg-primary py-3 text-sm font-medium text-white transition hover:bg-primary-light"
      >
        Back to the asset
      </button>
      <Link
        to={`/tokenize/assets/${assetId}`}
        className="block text-sm text-ink-muted hover:text-ink"
      >
        View all offerings
      </Link>
    </div>
  );
}

function Field({
  label,
  hint,
  error,
  className,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="text-sm font-medium text-ink">{label}</span>
      {hint && !error ? <span className="ml-1.5 text-micro text-ink-subtle">{hint}</span> : null}
      {error ? <span className="ml-1.5 text-micro text-danger">{error}</span> : null}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  inputMode,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'numeric';
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputMode={inputMode}
      className="h-11 w-full rounded-field border border-border bg-surface px-3 text-base text-ink outline-none transition placeholder:text-ink-subtle focus:border-primary focus:ring-2 focus:ring-primary/20"
    />
  );
}

function DateInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-11 w-full rounded-field border border-border bg-surface px-3 text-base text-ink outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
    />
  );
}

function BackIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
    </svg>
  );
}
